import { useState, useEffect, useRef } from "react";
import { Bell, X, Users, UserPlus, Calendar, AlertTriangle, Check } from "lucide-react";
import api from "../../services/api";

var TYPE_CONFIG = {
  lead: { icon: UserPlus, color: "text-blue-500", bg: "bg-blue-50" },
  client: { icon: Users, color: "text-green-500", bg: "bg-green-50" },
  agenda: { icon: Calendar, color: "text-purple-500", bg: "bg-purple-50" },
  parcela_atraso: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  var now = new Date();
  var d = new Date(dateStr);
  var diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "agora";
  if (diff < 3600) return Math.floor(diff / 60) + "min";
  if (diff < 86400) return Math.floor(diff / 3600) + "h";
  return Math.floor(diff / 86400) + "d";
}

function NotifItem({ n, onRead }) {
  var cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.lead;
  var Icon = cfg.icon;
  return (
    <div
      className={"flex items-start gap-2.5 px-3 py-2.5 cursor-pointer transition-colors " + (n.read ? "opacity-60" : "bg-blue-50/40 hover:bg-blue-50/70")}
      onClick={function () { if (!n.read) onRead(n.id); }}
      data-testid={"notif-item-" + n.id}
    >
      <div className={"p-1.5 rounded-lg flex-shrink-0 mt-0.5 " + cfg.bg}>
        <Icon className={"w-3.5 h-3.5 " + cfg.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-800 leading-tight">{n.title}</p>
        <p className="text-[11px] text-gray-500 mt-0.5 truncate">{n.message}</p>
        {n.broker_name && <p className="text-[10px] text-gray-400 mt-0.5">{n.broker_name}</p>}
      </div>
      <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">{timeAgo(n.created_at)}</span>
    </div>
  );
}

export default function NotificationBell() {
  var notifState = useState([]);
  var countState = useState(0);
  var openState = useState(false);
  var ref = useRef(null);

  function fetchCount() {
    api.get("/notifications/unread-count").then(function (r) { countState[1](r.data.count || 0); }).catch(function () {});
  }

  function fetchList() {
    api.get("/notifications?limit=30").then(function (r) { notifState[1](r.data || []); }).catch(function () {});
  }

  function triggerDaily() {
    api.post("/notifications/check-daily").catch(function () {});
  }

  useEffect(function () {
    fetchCount();
    triggerDaily();
    var interval = setInterval(fetchCount, 60000);
    return function () { clearInterval(interval); };
  }, []);

  useEffect(function () {
    if (openState[0]) fetchList();
  }, [openState[0]]);

  useEffect(function () {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) openState[1](false);
    }
    document.addEventListener("mousedown", handleClick);
    return function () { document.removeEventListener("mousedown", handleClick); };
  }, []);

  function markRead(id) {
    api.post("/notifications/" + id + "/read").then(function () {
      notifState[1](function (prev) {
        return prev.map(function (n) { return n.id === id ? Object.assign({}, n, { read: true }) : n; });
      });
      countState[1](function (c) { return Math.max(0, c - 1); });
    }).catch(function () {});
  }

  function markAllRead() {
    api.post("/notifications/read-all").then(function () {
      notifState[1](function (prev) { return prev.map(function (n) { return Object.assign({}, n, { read: true }); }); });
      countState[1](0);
    }).catch(function () {});
  }

  var count = countState[0];
  var open = openState[0];
  var notifs = notifState[0];

  return (
    <div className="relative" ref={ref}>
      <button
        className="relative p-2 sm:p-2.5 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
        onClick={function () { openState[1](!open); }}
        data-testid="notifications-button"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="notifications-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-sm font-bold text-gray-800">Notificacoes</h3>
            <div className="flex items-center gap-2">
              {count > 0 && (
                <button onClick={markAllRead} className="text-[11px] text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1" data-testid="mark-all-read">
                  <Check className="w-3 h-3" />Marcar todas lidas
                </button>
              )}
              <button onClick={function () { openState[1](false); }} className="p-1 hover:bg-gray-200 rounded-lg">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifs.length === 0 && (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Nenhuma notificacao</p>
              </div>
            )}
            {notifs.map(function (n) { return <NotifItem key={n.id} n={n} onRead={markRead} />; })}
          </div>
        </div>
      )}
    </div>
  );
}
